import type { CallMode } from './callModeButton';
import { FeatureName, Issue, useIsFeatureEnabled } from '@gripp/shared-logic';
import { StyleProp, ViewStyle } from 'react-native';
import { IssueHeaderClassic } from './issueHeaderClassic';
import { IssueHeaderCompact } from './issueHeaderCompact';

export type IssueHeaderProps = {
  issue: Issue;
  onChanged: (severity: string) => void;
  setShowFooter: (show: boolean) => void;
  titleStyle?: StyleProp<ViewStyle>;
  viewAssetInfo?: () => void;
  onIssueDelete: () => void;
  hideMenu?: boolean;
  onPrint?: () => Promise<void>;
  onBackPress?: () => void;
  onAssetPress?: () => void;
  assetButtonLabel?: string;
  onEditPress?: () => void;
  showAssetLink?: boolean;
  /**
   * Show the asset link even when the Issue is the leftmost column. The share
   * views are left-only layouts that navigate to a route instead of sliding.
   */
  forceShowAssetLink?: boolean;
  /**
   * Signed-out viewer: severity is not editable and the Settings / print /
   * overflow actions are withheld, since every one of them would FORBIDDEN.
   */
  readOnly?: boolean;
  onShare?: () => void;
  /** Starts or joins the group call on this Activity. Undefined hides the icon. */
  onCallPress?: (mode?: CallMode) => void;
  /** Base media URL for member avatars in the chat-options sheet. */
  mediaUrl?: string;
  /** Admin add-member flow for the chat-options sheet (mention message). */
  onAddMember?: (member: { id: string; name: string }) => void;
  /** Opens the workspace invite flow from the chat-options sheet. */
  onInvitePress?: () => void;
  /**
   * Asset chat thread panel: a note thread has no underlying
   * activity to configure, so the title renders without the type badge or
   * asset subtitle and is never a tap target into settings, regardless of
   * whether `onEditPress` is supplied. Default false — every existing caller
   * (the classic activity page, `IssueHeaderClassic`) is unaffected.
   */
  isNoteThread?: boolean;
};

// The condensed single-row bar (call icon + chat-options sheet, GRIPP #2988)
// ships behind the Communication Platform toggle; everyone else keeps the
// classic two-row header. useIsFeatureEnabled reads false until features load,
// which lands on the classic default — the intended fallback.
export const IssueHeader = (props: IssueHeaderProps) => {
  const isCommunicationPlatformEnabled = useIsFeatureEnabled(
    FeatureName.CommunicationPlatform
  );

  return isCommunicationPlatformEnabled ? (
    <IssueHeaderCompact {...props} />
  ) : (
    <IssueHeaderClassic {...props} />
  );
};
